import React, { startTransition, useEffect, useRef, useState } from "react";
import tampuuriWindowObject from "../tampuuriWindowObject.js";
import useDebouncedHashedParams from "../searchApp/hooks/useDebouncedHashCache.jsx";
import useAbortController from "../searchApp/hooks/useAbortController.jsx";
import useAlgolia from "../searchApp/hooks/useAlgolia.jsx";
import SelectLocation from "../searchApp/Components/SelectLocation.jsx";
import SelectRentals from "../searchApp/Components/SelectRentals.jsx";
import useHelpers from "../searchApp/hooks/useHelpers.jsx";
import LoadingSpinner from "../searchApp/LoadingSpinner/LoadingSpinner.jsx";

function App(props) {
  const { getData } = useAlgolia();
  const {
    translate,
    getSvgs,
    getFrontpageHeroCache,
    getArchiveURL,
    getFpHeroTranslationMap,
  } = tampuuriWindowObject();
  const { runAbortable } = useAbortController();
  const { constructHref } = useHelpers();
  const firstRenderRef = useRef(true);

  const [count, setCount] = useState(0);
  const [state, setState] = useState({});
  const [svgObjects, setSvgObjects] = useState(getSvgs());
  const [params, setParams] = useState({
    locations: [],
    rentalTypes: [],
    hitsPerPage: 1,
    exchangeStudents: false,
    rent: null,
    extraInfos: [],
    surfaceArea: null,
    page: null,
  });

  const { debouncedParams, hasCache, getCache, setCache } =
    useDebouncedHashedParams(params, 300);
  const [href, setHref] = useState(constructHref(params, getArchiveURL()));
  const sectionRef = useRef(null);

  useEffect(() => {
    let isSuccess = false;

    if (firstRenderRef.current) {
      // First render with pre-fetched cached data
      isSuccess = getCachedServerData();
      firstRenderRef.current = false;

      setTimeout(() => {
        const parent = sectionRef?.current?.parentNode;
        parent?.classList?.remove("skeleton-container");
      }, 300);
    }

    if (hasCache()) {
      // Has queried frontend data cached
      getCachedQueryData(debouncedParams);
      return;
    }

    if (!isSuccess && !firstRenderRef.current) {
      // If cache data was not correct --> Fetch data again
      getResults(debouncedParams);
    }
  }, [debouncedParams]);

  /**
   * useDebounceHashCache has already cached these results
   * Fast return the data without network request or any unnecessary state changes
   * @param debouncedParams
   */
  const getCachedQueryData = (debouncedParams) => {
    getCache(debouncedParams).then((cached) => {
      if (!cached.initialHits) {
        console.error(
          "Cached data failed to fetch, needs to fetch with request",
          cached,
        );
        getResults(debouncedParams);
        return;
      }

      startTransition(() => {
        setCount(cached.initialHits);
      });
    });
  };

  /**
   * Set states from cached data from server to window object
   * @return bool true if success and false on fail
   */
  const getCachedServerData = () => {
    const cachedAlgoliaData = getFrontpageHeroCache();

    if (!cachedAlgoliaData || !cachedAlgoliaData?.initialHits) {
      return false;
    }
    setCount(cachedAlgoliaData.initialHits);
    return true;
  };

  const getResults = (params) => {
    setState({ loading: true });

    let cached = {
      initialHits: null,
    };

    runAbortable(getData, params)
      .then((res) => {
        cached.initialHits = res.count;
        setCount(res.count);
      })
      .catch((e) => {
        if (e?.name === "AbortError") {
          return;
        }

        setState({ error: e });
      })
      .finally(() => {
        // Set frontend cache
        setCache(params, cached);
        setState({ loading: false });
      });
  };

  const submitParams = (newParams = {}) => {
    const final = { ...params, ...newParams };
    setParams(final);
    const URL = constructHref(final, getArchiveURL());
    setHref(URL.href);
  };

  const translateLabels = (string) => {
    const map = getFpHeroTranslationMap();
    if (!map) {
      return string;
    }

    if (map[string] !== undefined) {
      return map[string];
    }

    return string;
  };

  return (
    <div ref={sectionRef} className="query-items">
      <SelectLocation
        title={translate("Etsi asuntoa")}
        placeholder={translate("Valitse valikosta tai etsi hakusanalla")}
        params={params}
        submitParams={submitParams}
        svgObjects={svgObjects}
      />
      <div className="rentals-and-extras">
        <SelectRentals
          params={params}
          submitParams={submitParams}
          svgObjects={svgObjects}
          addExchange={true}
          translateLabels={translateLabels}
        />
      </div>

      <div className="flex-grow-1 flex-shrink-0 w-100">
        <a href={href} className="button button-basic">
          {translate("Näytä sopivat kohteet")}{" "}
          {state?.loading === true || isNaN(count) ? (
            <LoadingSpinner
              useSpinner="ClipLoader"
              overRideCss={{
                marginLeft: "1rem",
                width: "1rem",
                height: "1rem",
              }}
              color="#fff"
            />
          ) : (
            "(" + count + translate("kpl") + ")"
          )}
        </a>
      </div>
    </div>
  );
}

export default App;
